<template>
  <div class="modal-overlay" v-if="show" @click.self="cerrar">
    <div class="modal-dialog modal-xl modal-dialog-scrollable">
      <div class="modal-content shadow-lg">
        <!-- Header -->
        <div class="modal-header bg-white border-bottom">
          <div>
            <h5 class="mb-0 font-weight-bold">
              <i class="fa fa-plus-circle mr-2" :class="tipo === 'principal' ? 'text-success' : 'text-info'"></i>
              Asignar Membresía
              <span class="badge ml-2" :class="tipo === 'principal' ? 'badge-success' : 'badge-info'">
                {{ tipo === 'principal' ? 'Principal (Activa)' : 'Adicional (Secundaria)' }}
              </span>
            </h5>
            <small class="text-muted">
              Socio: <strong>{{ socio.nombreSocio }}</strong> · RUT <strong>{{ socio.rut }}</strong>
            </small>
          </div>
          <button type="button" class="close" @click="cerrar" :disabled="saving">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <!-- Paso 1: Filtros -->
          <section class="card border-0 shadow-sm mb-3">
            <div class="card-body">
              <h6 class="font-weight-bold mb-3">
                <i class="fa fa-filter text-primary mr-2"></i>
                1. Filtra los planes disponibles
              </h6>
              <div class="row">
                <div class="form-group col-md-4">
                  <label class="small text-muted mb-1">Familia</label>
                  <select class="form-control" v-model="filtros.idFamilia" @change="onFiltroFamiliaChange">
                    <option :value="null">— Todas las familias —</option>
                    <option v-for="f in familiasActivas" :key="f._id || f.id" :value="f.id">
                      {{ f.nombre }}
                    </option>
                  </select>
                </div>
                <div class="form-group col-md-4">
                  <label class="small text-muted mb-1">SubFamilia</label>
                  <select class="form-control" v-model="filtros.idSubFamilia" :disabled="!filtros.idFamilia">
                    <option :value="null">— Todas las subfamilias —</option>
                    <option v-for="sf in subfamiliasFiltradas" :key="sf._id || sf.id" :value="sf.id">
                      {{ sf.nombre }}
                    </option>
                  </select>
                </div>
                <div class="form-group col-md-4">
                  <label class="small text-muted mb-1">Descripción del plan (opcional)</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="filtros.descripcion"
                    placeholder="Ej: MENSUAL"
                    @keyup.enter="buscarPlanes(1)"
                  />
                </div>
              </div>
              <div class="d-flex justify-content-end">
                <button
                  type="button"
                  class="btn btn-outline-secondary mr-2"
                  @click="limpiarFiltros"
                  :disabled="loadingPlanes"
                >
                  <i class="fa fa-eraser mr-1"></i>
                  Limpiar filtros
                </button>
                <button
                  type="button"
                  class="btn btn-primary"
                  @click="buscarPlanes(1)"
                  :disabled="loadingPlanes"
                >
                  <i class="fa fa-search mr-1"></i>
                  Buscar planes
                </button>
              </div>
            </div>
          </section>

          <!-- Paso 2: Resultados -->
          <section class="card border-0 shadow-sm mb-3">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="font-weight-bold mb-0">
                  <i class="fa fa-list-ul text-primary mr-2"></i>
                  2. Selecciona un plan
                  <span class="text-muted small ml-2" v-if="!loadingPlanes && busquedaRealizada">
                    ({{ pagination.totalRecords || 0 }} resultados)
                  </span>
                </h6>
                <div v-if="planSeleccionado" class="small text-success">
                  <i class="fa fa-check-circle mr-1"></i>
                  Plan seleccionado: <strong>{{ planSeleccionado.descripcion }}</strong>
                </div>
              </div>

              <div v-if="loadingPlanes" class="text-center py-4">
                <div class="spinner-border text-primary" role="status"></div>
                <p class="mt-2 text-muted small mb-0">Buscando planes...</p>
              </div>

              <div v-else-if="!busquedaRealizada" class="text-center py-4 text-muted">
                <i class="fa fa-search fa-2x mb-2"></i>
                <p class="mb-0">Aplica filtros y haz click en <strong>Buscar planes</strong> para ver los disponibles.</p>
              </div>

              <div v-else-if="planes.length === 0" class="text-center py-4 text-muted">
                <i class="fa fa-info-circle fa-2x mb-2"></i>
                <p class="mb-0">No se encontraron planes con los filtros aplicados.</p>
              </div>

              <div v-else>
                <div class="row">
                  <div
                    v-for="plan in planes"
                    :key="plan._id || plan.id"
                    class="col-md-6 col-lg-4 mb-3"
                  >
                    <div
                      class="card h-100 plan-card"
                      :class="{ 'plan-card--selected': planSeleccionado && (planSeleccionado._id === plan._id || planSeleccionado.id === plan.id) }"
                      @click="seleccionarPlan(plan)"
                    >
                      <div class="card-body p-3">
                        <div class="d-flex justify-content-between align-items-start mb-2">
                          <h6 class="mb-0 font-weight-bold">{{ plan.descripcion }}</h6>
                          <span class="badge badge-outline-secondary">ID {{ plan.id }}</span>
                        </div>
                        <small class="text-muted d-block mb-2">
                          {{ plan.nombreFamilia }} · {{ plan.nombreSubFamilia }}
                        </small>
                        <div class="row mb-1">
                          <div class="col-6">
                            <small class="text-muted d-block">Valor</small>
                            <strong>{{ formatearMoneda(plan.valor) }}</strong>
                          </div>
                          <div class="col-6">
                            <small class="text-muted d-block">Duración</small>
                            <strong>
                              {{ plan.duracion || 0 }}
                              {{ (plan.unidadDuracionPlan && plan.unidadDuracionPlan.glosa) || '' }}
                            </strong>
                          </div>
                        </div>
                        <div class="d-flex flex-wrap mt-2">
                          <span v-if="plan.indicadorMatricula" class="badge badge-primary mr-1 mb-1">Matrícula</span>
                          <span v-if="plan.indicadorUsoSesiones" class="badge badge-info mr-1 mb-1">Sesiones</span>
                          <span v-if="plan.multiSede" class="badge badge-warning mr-1 mb-1">MultiSede</span>
                          <span v-if="plan.indicadorPermiteRenovar" class="badge badge-success mr-1 mb-1">Renovable</span>
                          <span v-if="plan.activo === false" class="badge badge-secondary mr-1 mb-1">Inactivo</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Paginación -->
                <div class="d-flex justify-content-between align-items-center mt-3" v-if="pagination.totalPages > 1">
                  <small class="text-muted">
                    Página <strong>{{ pagination.page }}</strong> de <strong>{{ pagination.totalPages }}</strong>
                  </small>
                  <div>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-secondary mr-2"
                      :disabled="!pagination.hasPrev || loadingPlanes"
                      @click="buscarPlanes(pagination.page - 1)"
                    >
                      <i class="fa fa-chevron-left"></i>
                      Anterior
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-secondary"
                      :disabled="!pagination.hasNext || loadingPlanes"
                      @click="buscarPlanes(pagination.page + 1)"
                    >
                      Siguiente
                      <i class="fa fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Paso 3: Sucursal y confirmar -->
          <section class="card border-0 shadow-sm mb-1" v-if="planSeleccionado">
            <div class="card-body">
              <h6 class="font-weight-bold mb-3">
                <i class="fa fa-building text-primary mr-2"></i>
                3. Sucursal de asignación
              </h6>
              <div class="row">
                <div class="form-group col-md-8">
                  <label class="small text-muted mb-1">Sucursal donde se asignará la membresía</label>
                  <select class="form-control" v-model="idSucursalSeleccionada">
                    <option :value="null">— Selecciona una sucursal —</option>
                    <option
                      v-for="suc in sucursales"
                      :key="suc._id"
                      :value="suc._id"
                    >
                      {{ suc.nombreSucursal }}
                    </option>
                  </select>
                  <small v-if="!sucursales.length" class="form-text text-warning">
                    No hay sucursales en sesión. Reinicia la sesión para refrescar el catálogo.
                  </small>
                </div>
                <div class="form-group col-md-4 d-flex align-items-end">
                  <small class="text-muted">
                    El catálogo proviene del filtro de Gestión de Socios (sesión actual).
                  </small>
                </div>
              </div>
            </div>
          </section>
        </div>

        <!-- Footer -->
        <div class="modal-footer bg-light">
          <button type="button" class="btn btn-outline-secondary" @click="cerrar" :disabled="saving">
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-success"
            :disabled="!puedeAsignar || saving"
            @click="asignar"
          >
            <span v-if="saving">
              <i class="fa fa-spinner fa-spin mr-2"></i>
              Asignando...
            </span>
            <span v-else>
              <i class="fa fa-check mr-2"></i>
              Asignar membresía
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const TURNO_DEFAULT = {
  idTurno: 1,
  descripcionTurno: 'SEMANA LIBRE',
  lunesAutorizado: true,
  lunesHoraInicio: '00:00:00',
  lunesHoraTermino: '23:59:00',
  martesAutorizado: true,
  martesHoraInicio: '00:00:00',
  martesHoraTermino: '23:59:00',
  miercolesAutorizado: true,
  miercolesHoraInicio: '00:00:00',
  miercolesHoraTermino: '23:59:00',
  juevesAutorizado: true,
  juevesHoraInicio: '00:00:00',
  juevesHoraTermino: '23:59:00',
  viernesAutorizado: true,
  viernesHoraInicio: '00:00:00',
  viernesHoraTermino: '23:59:00',
  sabadoAutorizado: true,
  sabadoHoraInicio: '00:00:00',
  sabadoHoraTermino: '23:59:00',
  domingoAutorizado: true,
  domingoHoraInicio: '00:00:00',
  domingoHoraTermino: '23:59:00'
};

function glosaOrString(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value.glosa) return value.glosa;
  return '';
}

export default {
  name: 'AsignarMembresiaModal',
  props: {
    show: { type: Boolean, default: false },
    tipo: { type: String, required: true, validator: v => ['principal', 'adicional'].includes(v) },
    socio: { type: Object, required: true },
    catalogos: {
      type: Object,
      default: () => ({ familias: [], subfamilias: [] })
    },
    sucursales: { type: Array, default: () => [] }
  },
  data() {
    return {
      filtros: {
        idFamilia: null,
        idSubFamilia: null,
        descripcion: ''
      },
      loadingPlanes: false,
      busquedaRealizada: false,
      planes: [],
      pagination: {
        page: 1,
        limit: 12,
        totalPages: 0,
        totalRecords: 0,
        hasPrev: false,
        hasNext: false
      },
      planSeleccionado: null,
      idSucursalSeleccionada: null,
      saving: false
    };
  },
  computed: {
    familiasActivas() {
      return (this.catalogos.familias || []).filter(f => f.activo !== false);
    },
    subfamiliasFiltradas() {
      const lista = (this.catalogos.subfamilias || []).filter(sf => sf.activo !== false);
      const idFamilia = this.filtros.idFamilia;
      if (!idFamilia) return [];
      return lista.filter(sf => {
        const idF =
          (sf.familia && sf.familia.id) ||
          (sf.familiaInfo && sf.familiaInfo.id) ||
          sf.idFamilia;
        return idF === idFamilia;
      });
    },
    puedeAsignar() {
      return !!(this.planSeleccionado && this.idSucursalSeleccionada);
    }
  },
  watch: {
    show(nuevo) {
      if (nuevo) {
        this.reset();
      }
    }
  },
  methods: {
    reset() {
      this.filtros = { idFamilia: null, idSubFamilia: null, descripcion: '' };
      this.loadingPlanes = false;
      this.busquedaRealizada = false;
      this.planes = [];
      this.pagination = {
        page: 1,
        limit: 12,
        totalPages: 0,
        totalRecords: 0,
        hasPrev: false,
        hasNext: false
      };
      this.planSeleccionado = null;
      this.idSucursalSeleccionada = null;
      this.saving = false;
    },
    cerrar() {
      if (this.saving) return;
      this.$emit('close');
    },
    onFiltroFamiliaChange() {
      this.filtros.idSubFamilia = null;
    },
    limpiarFiltros() {
      this.filtros = { idFamilia: null, idSubFamilia: null, descripcion: '' };
    },
    formatearMoneda(v) {
      if (v == null) return '—';
      try {
        return new Intl.NumberFormat('es-CL', {
          style: 'currency',
          currency: 'CLP',
          maximumFractionDigits: 0
        }).format(v);
      } catch (e) {
        return `$${v}`;
      }
    },
    seleccionarPlan(plan) {
      this.planSeleccionado = plan;
    },
    async buscarPlanes(page) {
      this.loadingPlanes = true;
      this.busquedaRealizada = true;
      const parameters = {
        page: page || 1,
        limit: this.pagination.limit,
        sortBy: 'descripcion',
        sortOrder: 'asc'
      };
      if (this.filtros.idFamilia) parameters.idFamilia = this.filtros.idFamilia;
      if (this.filtros.idSubFamilia) parameters.idSubFamilia = this.filtros.idSubFamilia;
      if (this.filtros.descripcion && this.filtros.descripcion.trim()) {
        parameters.descripcion = this.filtros.descripcion.trim();
      }

      try {
        const response = await this.$CentralGymOPS_API.post('', {
          Header: { Operacion: '/v1/planes/consultar-paginados' },
          Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
          Parameters: parameters,
          Body: {}
        });
        const dataOut = (response.data && response.data.dataOut) || {};
        this.planes = dataOut.planes || [];
        const pag = dataOut.pagination || {};
        const currentPage = pag.page || parameters.page;
        const totalPages = pag.totalPages || 0;
        this.pagination = {
          page: currentPage,
          limit: pag.limit || this.pagination.limit,
          totalPages,
          totalRecords: dataOut.total || 0,
          hasPrev: pag.hasPrev !== undefined ? pag.hasPrev : currentPage > 1,
          hasNext: pag.hasNext !== undefined ? pag.hasNext : currentPage < totalPages
        };
      } catch (error) {
        const backendMessage =
          error.response &&
          error.response.data &&
          error.response.data.result &&
          error.response.data.result.message;
        this.$swal.fire({
          title: 'Error al buscar planes',
          text: backendMessage || error.message || 'Error inesperado',
          icon: 'error',
          confirmButtonColor: '#6bd098'
        });
        this.planes = [];
      } finally {
        this.loadingPlanes = false;
      }
    },
    buildBody() {
      const p = this.planSeleccionado || {};
      const ia = p.informacionAdicional || {};
      const turno = p.turno && Object.keys(p.turno).length ? p.turno : TURNO_DEFAULT;
      const fechaAsignacionISO = new Date().toISOString().slice(0, 19);
      return {
        idFamilia: p.idFamilia,
        nombreFamilia: p.nombreFamilia,
        idSubFamilia: p.idSubFamilia,
        nombreSubFamilia: p.nombreSubFamilia,
        idPlan: p.id,
        descripcionPlan: p.descripcion,
        valorPlan: p.valor != null ? p.valor : 0,
        tipoIVA: p.tipoIVA || 'EXENTO',
        indicadorMatricula: !!p.indicadorMatricula,
        valorMatricula: p.valorMatricula || 0,
        fechaPagoMatricula: p.fechaPagoMatricula || '',
        indicadorFactura: !!p.indicadorFactura,
        diasPermitidoCongelar: p.diasPermitidoCongelar || 0,
        duracionPlan: p.duracion || 0,
        unidadDuracionPlan: glosaOrString(p.unidadDuracionPlan),
        tipoPlan: glosaOrString(p.convenio) || 'NORMAL',
        porcentajeDescuento: p.porcentajeDescuento || 0,
        indicadorMesCalendario: p.indicadorMesCalendario !== false,
        diasMesNoCalendario: p.diasMesNoCalendario || 0,
        indicadorUsoSesiones: !!p.indicadorUsoSesiones,
        sesionesLimite: p.sesionesLimite || 0,
        sesionesUtilizadas: 0,
        indicadorPermiteRenovar: p.indicadorPermiteRenovar !== false,
        expiracionRenovacion: p.expiracionRenovacion || 0,
        unidadExpiracionRenovacion: glosaOrString(p.unidadExpiracionRenovacion),
        fechaAsignacion: fechaAsignacionISO,
        fechaInicio: '',
        fechaVencimiento: '',
        fechaVigencia: '',
        fechaUltimoPago: '',
        horaUltimoPago: '',
        codigoSucursalUltimoPago: '',
        sucursalUltimoPago: '',
        estadoPlan: 'PENDIENTE',
        turno,
        informacionAdicional: {
          multiSede: ia.multiSede !== undefined ? !!ia.multiSede : !!p.multiSede,
          descuentoSesionesXDia: ia.descuentoSesionesXDia !== false,
          esSuscripcion: !!ia.esSuscripcion,
          limitarPuntoAcceso: !!ia.limitarPuntoAcceso,
          puntoAccesoPermitidos: Array.isArray(ia.puntoAccesoPermitidos)
            ? ia.puntoAccesoPermitidos.map(pa => ({
                idAcceso: pa.idAcceso,
                descontarSesion: !!pa.descontarSesion
              }))
            : [],
          beneficiosAdicionales: Array.isArray(ia.beneficiosAdicionales)
            ? [...ia.beneficiosAdicionales]
            : []
        }
      };
    },
    async asignar() {
      if (!this.puedeAsignar) return;
      const sucursal = this.sucursales.find(s => s._id === this.idSucursalSeleccionada);
      if (!sucursal) {
        this.$swal.fire({
          title: 'Sucursal inválida',
          text: 'La sucursal seleccionada no se encontró en el catálogo.',
          icon: 'warning',
          confirmButtonColor: '#6bd098'
        });
        return;
      }

      const tipoPlan = this.tipo === 'principal' ? 'PRINCIPAL' : 'SECUNDARIO';
      const tipoLabel = this.tipo === 'principal' ? 'Principal (Activa)' : 'Secundaria (Adicional)';

      const confirm = await this.$swal.fire({
        title: '¿Asignar membresía?',
        html:
          `<div class="text-left">` +
          `<p class="mb-2">Vas a asignar la siguiente membresía a <strong>${this.socio.nombreSocio || ''}</strong> (RUT <strong>${this.socio.rut}</strong>):</p>` +
          `<ul class="mb-0">` +
          `<li><strong>Tipo:</strong> ${tipoLabel}</li>` +
          `<li><strong>Plan:</strong> ${this.planSeleccionado.descripcion}</li>` +
          `<li><strong>Familia / Sub:</strong> ${this.planSeleccionado.nombreFamilia} · ${this.planSeleccionado.nombreSubFamilia}</li>` +
          `<li><strong>Sucursal:</strong> ${sucursal.nombreSucursal}</li>` +
          `</ul>` +
          `</div>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, asignar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#6bd098',
        cancelButtonColor: '#eb6532'
      });
      if (!confirm.isConfirmed) return;

      this.saving = true;
      try {
        const response = await this.$CentralGymOPS_API.post('', {
          Header: { Operacion: '/v1/membresias/socio/ingresar' },
          Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
          Parameters: {
            idSucursal: sucursal._id,
            nombreSucursal: sucursal.nombreSucursal,
            rut: this.socio.rut,
            tipoPlan,
            modalidadPlan: 'ON-LINE'
          },
          Body: this.buildBody()
        });
        const result = response.data && response.data.result;
        if (result && result.code === 0) {
          await this.$swal.fire({
            title: '¡Membresía asignada!',
            text: result.message || 'La membresía se asignó correctamente.',
            icon: 'success',
            confirmButtonColor: '#6bd098'
          });
          this.$emit('saved');
        } else {
          throw new Error((result && result.message) || 'No se pudo asignar la membresía');
        }
      } catch (error) {
        const backendMessage =
          error.response &&
          error.response.data &&
          error.response.data.result &&
          error.response.data.result.message;
        this.$swal.fire({
          title: 'Error al asignar la membresía',
          text: backendMessage || error.message || 'Error inesperado',
          icon: 'error',
          confirmButtonColor: '#6bd098'
        });
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2rem 1rem;
  overflow-y: auto;
}
.modal-dialog {
  width: 100%;
  max-width: 1100px;
  margin: 0;
}
.modal-content {
  border-radius: 8px;
  background: #fff;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 4rem);
}
.modal-header,
.modal-footer {
  flex-shrink: 0;
  padding: 1rem 1.5rem;
}
.modal-body {
  overflow-y: auto;
  padding: 1.25rem 1.5rem;
}
.plan-card {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  border: 2px solid transparent;
}
.plan-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.08);
}
.plan-card--selected {
  border-color: #6bd098;
  background: #f3fbf6;
}
.badge-outline-secondary {
  color: #6c757d;
  background: transparent;
  border: 1px solid #6c757d;
}
.close {
  background: transparent;
  border: 0;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
}
</style>
